import { Grid } from '@material-ui/core';
import { isEmpty, isEqual } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IUser } from '~/src/dash/api/console-api';
import {
  IIASFirstAndThirdPartyRequestBody,
  queryFirstAndThirdPartyIasData,
} from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import { IMeasurementTableRowData, ListTable } from '~/src/dash/components/list-table';
import {
  extractDataKeysFromColumns,
  generateListTableColumns,
  generateListTableData,
} from '~/src/dash/views/common/ias-data-consts';
import { IASMeasurementTrackEvent } from '~/src/dash/views/measurement/common/consts';
import { IMeasurementQueryParamsNoComp } from '~/src/dash/views/measurement/common/types';
import { IMeasurementTable, IMeasurementTableGroupBy } from '~/src/dash/views/measurement/config';
import { ISnackbarState } from '~/src/dash/views/snackbar-view';

interface IMeasurementTableProps {
  tableInfo: IMeasurementTable;
  queryParams: IMeasurementQueryParamsNoComp;
  currentGroupBy: IMeasurementTableGroupBy;
  user: IUser;
  handleOrderOnChange: (id: number) => void;
  handleNameLinkClickInfo?: (name: string, id: string) => void;
  setSnackbar: (snackbar: ISnackbarState) => void;
  handlePagination: (pageNumber: number, pageSize: number) => void;
  handleTableSearch: (nameSearch: string, idSearch: string) => void;
  chartDataKey: string;
}

export const MeasurementTable = ({
  tableInfo,
  queryParams,
  currentGroupBy,
  user,
  handleOrderOnChange,
  handleNameLinkClickInfo,
  setSnackbar,
  handlePagination,
  handleTableSearch,
  chartDataKey,
}: IMeasurementTableProps) => {
  const isMounted = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [params, setParams] = useState(queryParams);
  const [groupBy, setGroupBy] = useState(currentGroupBy);
  const [data, setData] = useState<any>(null);
  const [currentPageSize, setCurrentPageSize] = useState(10);

  const { graph, columns } = tableInfo;

  const {
    startDate,
    endDate,
    timezone,
    groupingNumber,
    deviceTypes,
    teamID,
    anIDs,
    publisherID,
    filters,
    advertiserIDs,
    orderIDs,
    lineItemIDs,
    creativeIDs,
    adUnitIDs,
  } = params;

  useEffect(() => {
    setIsLoading(true);
  }, []);

  useEffect(() => {
    if (!isEmpty(queryParams)) {
      unstable_batchedUpdates(() => {
        setIsLoading(true);
        setParams({ ...queryParams });
        if (!isEqual(groupBy, currentGroupBy)) {
          setGroupBy({ ...currentGroupBy });
        }
      });
    }
  }, [queryParams, currentGroupBy, chartDataKey]);

  // once a new current group by is set, we set isLoading to false in order to run queryData
  useEffect(() => {
    if (isMounted.current) {
      setIsLoading(false);
    }
  }, [params, groupBy]);

  // once data is set the first time, we set mounted to true
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  }, [data]);

  const queryData = (
    query: any,
  ): Promise<{ data: IMeasurementTableRowData[]; page: number; totalCount: number } | null> => {
    return new Promise((resolve) => {
      const { search, pageSize, orderDirection } = query;
      const nameSearch = search as string;
      const idSearch = /^-?\d+$/.test(search) ? search.toString() : '';
      const currentPage = query.page;
      setCurrentPageSize(pageSize);
      handlePagination(query.page, pageSize);
      handleTableSearch(nameSearch, idSearch);

      const body1 = {
        GraphID: graph,
        StartDate: startDate,
        EndDate: endDate,
        Timezone: timezone,
        GroupingNumber: groupingNumber,
        DeviceTypes: deviceTypes,
        TeamID: teamID,
        AnIDs: anIDs,
        Filters: filters,
        AdvertiserIDs: advertiserIDs,
        OrderIDs: orderIDs,
        LineItemIDs: lineItemIDs,
        CreativeIDs: creativeIDs,
        AdUnitIDs: adUnitIDs,
        NameSearch: nameSearch,
        IDSearch: idSearch,
        ReportOrderByKey: chartDataKey,
        ReportOrderBy: orderDirection || 'desc',
        GroupByKey: groupBy.paramKey,
        Page: currentPage,
        PageSize: pageSize,
      } as IIASFirstAndThirdPartyRequestBody;

      const queryStartTime = moment().unix();
      const queryDurationDays = moment(endDate).diff(moment(startDate), 'days') + 1;
      queryFirstAndThirdPartyIasData(body1, { publisher_id: publisherID })
        .then((data: any) => {
          const queryDuration = moment().unix() - queryStartTime;
          const data1 = data[0].rows;
          let queryFailed = false;
          setData(data1 ? data1 : []);
          // if the call is successful but there is no data
          if (!data1) {
            queryFailed = true;
            setSnackbar({
              message: `We are not tracking any measurement data here...\nPlease adjust the filters / date range combination`,
              warning: true,
              error: false,
            });
          }
          const columnKeys = extractDataKeysFromColumns(columns);
          const listTableData = generateListTableData(groupBy, columnKeys, data1);
          IASMeasurementTrackEvent(user, {
            eventTitle: 'IAS Measurement Drill Dashboard - Run Table Agg Query',
            graphID: parseInt(tableInfo.graph.replace(/\D/g, ''), 10),
            startDate,
            endDate,
            timezone,
            queryDuration,
            queryFailed,
            queryDurationDays,
          });
          resolve({
            data: listTableData,
            page: currentPage,
            totalCount: data1[0].TotalRows,
          });
        })
        .catch(() => {
          const queryDuration = moment().unix() - queryStartTime;
          setData([]);
          IASMeasurementTrackEvent(user, {
            eventTitle: 'IAS Measurement Drill Dashboard - Run Table Agg Query',
            graphID: parseInt(tableInfo.graph.replace(/\D/g, ''), 10),
            startDate,
            endDate,
            timezone,
            queryDuration,
            queryFailed: true,
            queryDurationDays,
          });
          setSnackbar({
            message: 'Something went wrong: Please try again.',
            error: true,
            warning: false,
          });
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        });
    });
  };

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item>
        <ListTable
          title=''
          columns={generateListTableColumns(groupBy, columns)}
          data={[]}
          queryData={queryData}
          handleNameLinkClickInfo={handleNameLinkClickInfo}
          enablePagination={true}
          pageSize={currentPageSize}
          pageSizeOptions={[10, 25, 50]}
          isLoading={isLoading}
          disableSnackbar
          emptyRowsWhenPaging={true}
          disableUpdateFocus={true}
          onOrderChange={handleOrderOnChange}
          enableSearchBarLeft
        />
      </Grid>
    </Grid>
  );
};
